import {
  ALL_MODULES,
  GET_CONSTANTS,
  ALL_SHARD_MODULES
} from '@nile/components';
import { timezones } from '@nile/banyan/src/Constants/timezone';
import {
  AccountClient,
  TicketClient,
  GET_TECHNICIANS_BY_CRITERIA,
  GET_ALL_TECH_GROUP
} from '@nile/graphql';
import {
  GET_ALL_CUSTOM_FIELDS,
  GET_ALL_FIELDS_V2
} from '@nile/banyan/src/Graphql/Mutations/ticket';
import { GET_ALL_CLIENT_DATA } from '../dromo';

const STATE_LIST_JSON_FILE_NAME = 'states';
const COUNTRY_LIST_JSON_FILE_NAME = 'country';

export const getQuery = (field) => {
  switch (field) {
    case 'accountManager':
      return GET_TECHNICIANS_BY_CRITERIA;
    case 'customFields':
      return GET_ALL_FIELDS_V2;
    default:
      return null;
  }
};

export const getClient = (field) => {
  switch (field) {
    case 'customFields':
      return AccountClient;
    default:
      return AccountClient;
  }
};

export const getVariables = (field) => {
  switch (field) {
    case 'accountManager':
      return {
        input: { listInfo: {} }
      };
    case 'customFields':
      return {
        input: ALL_SHARD_MODULES.client
      };
    default:
      return undefined;
  }
};

export const callQuery = (field) => {
  const query = getQuery(field);
  const client = getClient(field);
  const variables = getVariables(field);

  switch (field) {
    case 'accountManager':
    case 'customFields':
      return client?.query({ query, variables });
    default:
      return [];
  }
};
export const getSelectOptions = (field) => {
  switch (field) {
    case 'hqSiteTimezoneCode':
      return timezones
        .reduce((acc, zone) => [...acc, ...zone.utc], [])
        .map((zoneCode) => ({ value: zoneCode, label: zoneCode }));
    default:
      return [];
  }
};

const getQueryName = (field) => {
  switch (field) {
    case 'accountManager':
      return 'getTechnicianList';
    default:
      return '';
  }
};

export const shapeOptions = (field, options) => {
  const key = getQueryName(field);
  switch (field) {
    case 'accountManager':
      return options?.data?.[key].userList.map((el) => {
        return {
          value: `${el.userId}`,
          label: `${el.name}`
        };
      });
    case 'hqSiteState':
      return options.map((state) => ({
        value: state.isoCode,
        label: state.name,
        isoCode: state.isoCode,
        name: state.name
      }));
    case 'hqSiteCountry':
      return options.map((country) => ({
        value: country.isoCode,
        label: country.name,
        isoCode: country.isoCode,
        name: country.name
      }));
    default:
      return options;
  }
};

export const getClientValidationFields = () => {
  return {
    allTechGroups: AccountClient.query({
      query: GET_ALL_TECH_GROUP
    }),
    usedValues: AccountClient.query({
      query: GET_ALL_CLIENT_DATA,
      variables: { listInfo: {} }
    }),
    customFields: AccountClient.query({
      query: GET_ALL_FIELDS_V2,
      variables: { input: ALL_SHARD_MODULES.client }
    })
  };
};

export const modelClientValidationFields = (type, { data }) => {
  switch (type) {
    case 'allTechGroups':
      return data?.getTechnicianGroups.map(({ name }) => name) || [];
    case 'usedValues':
      if (data?.getClientList) {
        let techGroups = [];
        let emailDomains = [];
        let primaryContactEmails = [];
        data.getClientList.clients.forEach((client) => {
          techGroups = [
            ...techGroups,
            ...client.technicianGroups.map((techGroup) => ({
              ...techGroup,
              clientName: client.name
            }))
          ];

          emailDomains = [
            ...emailDomains,
            ...client.emailDomains.map((emailDomain) => ({
              emailDomain,
              clientName: client.name
            }))
          ];

          primaryContactEmails = [
            ...primaryContactEmails,
            {
              ...client.primaryContact,
              clientName: client.name
            }
          ];
        });
        return {
          techGroups,
          emailDomains,
          primaryContactEmails
        };
      }
      return {};
    case 'customFields':
      return (
        data?.getAllFields?.filter(
          (field) => field?.fieldCategory === 'CUSTOM'
        ) || []
      );
    default:
      return null;
  }
};

export const getClientImportFields = () => {
  const fields = [
    {
      label: 'dromo.title.clientName',
      key: 'name',
      validators: [
        {
          validate: 'required',
          errorMessage: 'dromo.validation.requiredField'
        },
        {
          validate: 'unique_case_insensitive',
          errorMessage: 'dromo.validation.unique'
        }
      ]
    },
    {
      label: 'dromo.title.hqSiteName',
      key: 'hqSiteName',
      validators: [
        {
          validate: 'require_with',
          fields: [
            'hqSiteLine1',
            'hqSiteLine2',
            'hqSiteLine3',
            'hqSiteCountry',
            'hqSiteState',
            'hqSiteCity',
            'hqSitePostalCode',
            'hqSiteTimezoneCode'
          ],
          errorMessage: 'dromo.validation.requiredField'
        }
      ]
    },
    {
      label: 'dromo.title.stage',
      key: 'stage'
    },
    {
      label: 'dromo.title.status',
      key: 'status'
    },
    {
      label: 'dromo.title.hqSiteLine1',
      key: 'hqSiteLine1'
    },
    {
      label: 'dromo.title.hqSiteLine2',
      key: 'hqSiteLine2'
    },
    {
      label: 'dromo.title.hqSiteLine3',
      key: 'hqSiteLine3'
    },
    {
      label: 'dromo.title.hqSiteCountry',
      key: 'hqSiteCountry',
      type: 'select',
      selectOptions: GET_CONSTANTS(COUNTRY_LIST_JSON_FILE_NAME)
    },
    {
      label: 'dromo.title.hqSiteState',
      key: 'hqSiteState',
      type: 'select',
      selectOptions: GET_CONSTANTS(STATE_LIST_JSON_FILE_NAME)
    },
    {
      label: 'dromo.title.hqSiteCity',
      key: 'hqSiteCity'
    },
    {
      label: 'dromo.title.hqSitePostalCode',
      key: 'hqSitePostalCode',
      validators: [
        {
          validate: 'regex_match',
          regex: '^([0-9a-zA-Z]+[ ]{0,1})+$',
          errorMessage: 'dromo.validation.alphaNumeric'
        }
      ]
    },
    {
      label: 'dromo.title.hqSiteTimezoneCode',
      key: 'hqSiteTimezoneCode',
      type: 'select',
      selectOptions: getSelectOptions('hqSiteTimezoneCode')
    },
    {
      label: 'dromo.title.emailDomains',
      key: 'emailDomains',
      validators: [
        {
          validate: 'unique_case_insensitive',
          errorMessage: 'dromo.validation.unique'
        }
      ]
    },
    {
      label: 'dromo.title.primaryContactFirstName',
      key: 'primaryContactFirstName',
      validators: [
        {
          validate: 'required',
          errorMessage: 'dromo.validation.requiredField'
        },
        {
          validate: 'require_with',
          fields: ['primaryContactEmail'],
          errorMessage: 'dromo.validation.requiredField'
        }
      ]
    },
    {
      label: 'dromo.title.primaryContactLastName',
      key: 'primaryContactLastName',
      validators: [
        {
          validate: 'required',
          errorMessage: 'dromo.validation.requiredField'
        }
      ]
    },
    {
      label: 'dromo.title.primaryContactPhoneNumber',
      key: 'primaryContactPhoneNumber'
    },
    {
      label: 'dromo.title.primaryContactEmail',
      key: 'primaryContactEmail',
      validators: [
        {
          validate: 'required',
          errorMessage: 'dromo.validation.requiredField'
        },
        {
          validate: 'regex_match',
          // prettier-ignore
          regex: '^([a-zA-Z0-9_+\.\-])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$',
          errorMessage: 'domain.validation.emailAddress'
        }
      ]
    },
    {
      label: 'dromo.title.accountManager',
      key: 'accountManager',
      type: 'select',
      selectOptions: getSelectOptions('accountManager')
    },
    {
      label: 'dromo.title.technicianGroups',
      key: 'technicianGroups'
    }
  ];
  return fields;
};
export default getSelectOptions;
